import { Building2, CalendarClock, Hash, Plus, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { StatCard } from "@/client/components/stat-card";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import {
  type StaffMaintenanceWindow,
  maintenanceAdminApi,
} from "@/client/features/admin/maintenance-admin-api";
import { type AdminTenant, tenantsAdminApi } from "@/client/features/admin/tenants-admin-api";
import { ApiError } from "@/client/lib/api";
import { formatDateTime, formatRelativeTime } from "@/client/lib/format";

function isUpcoming(w: StaffMaintenanceWindow): boolean {
  return w.status === "scheduled" || w.status === "in_progress";
}

function StatusBadge({ status }: { status: StaffMaintenanceWindow["status"] }) {
  const variant = status === "in_progress" ? "default" : "secondary";
  const label = status === "in_progress" ? "In progress" : "Scheduled";
  return <Badge variant={variant}>{label}</Badge>;
}

export function StaffDashboard() {
  // Maintenance endpoints are staff-admin-only (requireStaffAdmin); plain staff
  // would get a 403. Gate the maintenance fetch + UI on the role so the shared
  // overview still works for non-admin staff, who only see tenant-readable data.
  const isStaffAdmin = useAuthStore((s) => s.user?.staffRole === "admin");
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [windows, setWindows] = useState<StaffMaintenanceWindow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [t, m] = await Promise.all([
          tenantsAdminApi.list(),
          isStaffAdmin ? maintenanceAdminApi.list() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setTenants(t.tenants);
        setWindows(m?.windows ?? []);
        setError(false);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load the console.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStaffAdmin]);

  const upcoming = (windows ?? [])
    .filter(isUpcoming)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const slackMapped = (tenants ?? []).filter((t) => t.slackChannelId).length;
  const loadFailed = error && tenants === null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Staff console" description="Cross-tenant operations at a glance.">
        {isStaffAdmin ? (
          <Button asChild>
            <Link to="/admin/maintenance">
              <Plus className="size-4" />
              Schedule maintenance
            </Link>
          </Button>
        ) : null}
      </PageHeader>

      {loadFailed ? (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            Couldn't load the console. Refresh to try again.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              to="/admin/tenants"
              label="Tenants"
              value={tenants === null ? null : tenants.length}
              hint="Customer organizations"
              icon={Building2}
            />
            {isStaffAdmin ? (
              <StatCard
                to="/admin/maintenance"
                label="Upcoming maintenance"
                value={windows === null ? null : upcoming.length}
                hint="Scheduled or in progress"
                icon={Wrench}
              />
            ) : null}
            <StatCard
              to="/admin/tenants"
              label="Slack channels"
              value={tenants === null ? null : slackMapped}
              hint="Tenants mapped for alerts"
              icon={Hash}
            />
          </div>

          {isStaffAdmin ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CalendarClock className="text-muted-foreground size-4" />
                  Upcoming maintenance
                </CardTitle>
                <CardDescription>
                  The next windows customers will be notified about.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {windows === null ? (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : upcoming.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No maintenance scheduled.{" "}
                    <Link
                      to="/admin/maintenance"
                      className="text-foreground underline underline-offset-4"
                    >
                      Schedule one
                    </Link>
                    .
                  </p>
                ) : (
                  <ul className="flex flex-col divide-y">
                    {upcoming.slice(0, 4).map((w) => (
                      <li
                        key={w.id}
                        className="flex items-center justify-between gap-3 py-2 first:pt-0"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{w.title}</p>
                          <p className="text-muted-foreground text-xs">
                            {w.status === "in_progress"
                              ? formatDateTime(w.startsAt)
                              : `${formatRelativeTime(w.startsAt)} · ${formatDateTime(w.startsAt)}`}
                            {w.audience === "all"
                              ? " · All tenants"
                              : ` · ${w.tenantTargets.length} tenant${w.tenantTargets.length === 1 ? "" : "s"}`}
                          </p>
                        </div>
                        <StatusBadge status={w.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {isStaffAdmin ? (
                <Button variant="outline" asChild>
                  <Link to="/admin/maintenance">
                    <Wrench className="size-4" />
                    Maintenance
                  </Link>
                </Button>
              ) : null}
              <Button variant="outline" asChild>
                <Link to="/admin/tenants">
                  <Building2 className="size-4" />
                  Tenants
                </Link>
              </Button>
              {isStaffAdmin ? (
                <Button variant="outline" asChild>
                  <Link to="/admin/docs">Docs</Link>
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
